import { type ReactElement, type ReactNode, useState } from 'react';
import { Pressable, ScrollView, Text, View, useWindowDimensions } from 'react-native';

import type { makeStyles } from './loggerStyles';
import { stateAria } from "../a11y";

// Read-only coach-notes panel that drops below the logger header when «Info»
// is tapped (#318). Surfaces what the program/plan author already wrote but that
// the logger never showed: the program description + this day's session note.
// Display-only — the data is FK-resolved at render (never persisted).
//
// Accordion: each note is a collapsed row by default (label + chevron); the
// athlete taps the one they want to read (Antenore 2026-06-06). Both can be
// open at once. Shared by both loggers (card + timeline render the same
// LoggerHeader).
type CoachNotes = { programDescription?: string; sessionNote?: string };

type Props = {
  coachNotes: CoachNotes;
  /** The plan's or program's name, when the workout came from one. */
  title?: string;
  /** #386: the date and duration card, above the notes. */
  dateEditor?: ReactNode;
  styles: ReturnType<typeof makeStyles>;
  t: (k: string, opts?: Record<string, unknown>) => string;
};

function NoteAccordion({
  label,
  body,
  styles,
}: {
  label: string;
  body: string;
  styles: ReturnType<typeof makeStyles>;
}): ReactElement {
  const [open, setOpen] = useState(false);
  return (
    <View style={styles.notesPanelSection}>
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        {...stateAria({ expanded: open })}
        accessibilityLabel={label}
        onPress={() => setOpen((v) => !v)}
        hitSlop={6}
        style={({ pressed }) => [
          styles.notesAccordionHeader,
          pressed && styles.headerIconButtonPressed,
        ]}
      >
        <Text style={styles.notesPanelLabel}>{label}</Text>
        <Text style={styles.notesAccordionChevron}>{open ? '▾' : '▸'}</Text>
      </Pressable>
      {open ? <Text style={styles.notesPanelText}>{body}</Text> : null}
    </View>
  );
}

export function WorkoutNotesPanel({
  coachNotes,
  title,
  dateEditor,
  styles,
  t,
}: Props): ReactElement {
  // The cap grows with the font (as Dialog does, capped at 2x) but never past
  // 60% of the screen, so the header and the list stay in reach.
  const { height, fontScale } = useWindowDimensions();
  const maxHeight = Math.min(
    Math.round(320 * Math.min(fontScale, 2)),
    Math.round(height * 0.6),
  );
  const empty = !coachNotes.programDescription && !coachNotes.sessionNote;
  return (
    <View style={styles.notesPanel} accessibilityRole="summary">
      <ScrollView
        style={[styles.notesPanelScroll, { maxHeight }]}
        contentContainerStyle={styles.notesPanelScrollBody}
        showsVerticalScrollIndicator
      >
        {dateEditor}
        {title ? (
          <Text style={styles.notesPanelTitle} accessibilityRole="header">
            {title}
          </Text>
        ) : null}
        {empty ? (
          <Text style={styles.notesPanelText}>
            {t('mobile.logger.notesEmpty', {
              defaultValue: 'No notes for this session',
            })}
          </Text>
        ) : null}
        {coachNotes.programDescription ? (
          <NoteAccordion
            label={t('mobile.logger.notesProgramLabel', {
              defaultValue: 'Program',
            })}
            body={coachNotes.programDescription}
            styles={styles}
          />
        ) : null}
        {coachNotes.sessionNote ? (
          <NoteAccordion
            label={t('mobile.logger.notesWorkoutLabel', {
              defaultValue: 'This workout',
            })}
            body={coachNotes.sessionNote}
            styles={styles}
          />
        ) : null}
      </ScrollView>
    </View>
  );
}
